/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UsersService } from '../../users.service';
import { NgForm, UntypedFormGroup } from '@angular/forms';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ToastMessageOptions } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { UserPasswordChangeDtoModel } from '../user-password-change-dto.model';
import { Observable } from 'rxjs';
import { UserModel } from '../user.model';

@Component({
  selector: 'auth-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  @Input() isNewUser: boolean | undefined = undefined;
  @ViewChild('form') form: NgForm | undefined = undefined;

  userForm: UntypedFormGroup | undefined = undefined;
  userHasEmail: boolean | undefined = undefined;
  messages: ToastMessageOptions[] = [];
  emailValue: string | null = null;
  userData: UserModel | undefined = undefined;

  private unsubscribe: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly usersService: UsersService,
    private readonly location: Location,
    private readonly translateService: TranslateService
  ) {
    this.userForm = new UntypedFormGroup({});
  }

  ngOnInit() {
    this.getUserDataFromRouteParams();
  }

  loadUserData(userUuid: string) {
    return this.usersService.getUserById(userUuid).pipe(this.updateUserData());
  }

  private getUserDataFromRouteParams() {
    this.route.params
      .pipe(
        takeUntil(this.unsubscribe),
        filter((data) => data && data['uuid']),
        switchMap((data) => this.loadUserData(data['uuid'])),
        this.processUserData()
      )
      .subscribe();
  }

  private processUserData() {
    return (source: Observable<UserModel>) =>
      source.pipe(
        tap((userData) => {
          this.isNewUser = userData.isNewUser;
          if (userData.email) {
            this.userHasEmail = true;
            this.emailValue = userData?.email;
          } else {
            this.pushWarningEmailNullReference();
          }
        })
      );
  }

  private updateUserData() {
    return (source: Observable<UserModel>) =>
      source.pipe(
        tap((userData) => {
          if (userData) {
            this.userData = userData;
          }
        })
      );
  }

  loadUserDetails(userUuid: string) {
    this.loadUserData(userUuid).subscribe();
  }

  goBack() {
    if (this.userHasEmail) {
      this.router.navigate([`/`]);
    } else {
      this.goToLogin();
    }
  }

  save() {
    this.form?.form.markAllAsTouched();
    if (this.form?.valid) {
      this.usersService.saveUserProfile(this.userData).subscribe(() => {
        this.goToLogin(true);
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(true);
  }

  back() {
    this.location.back();
  }

  changePassword(userPasswordChangeDtoModel: UserPasswordChangeDtoModel) {
    this.usersService.changePassword(userPasswordChangeDtoModel)?.subscribe({
      next: () => {
        this.goToLogin(true);
      },
    });
  }

  private goToLogin(profileInfoChanged = false) {
    if (profileInfoChanged) {
      this.router.navigate([`/auth/login`], {
        queryParams: { profileInfoChanged: profileInfoChanged },
      });
    } else {
      this.router.navigate([`/auth/login`]);
    }
  }

  private pushWarningEmailNullReference() {
    this.messages = [];
    this.translateService.stream(`auth.warnings.emailNullReference`).subscribe((res: string) => {
      this.messages.push({
        severity: 'warn',
        summary: res,
      });
    });
  }
}
